import { useEffect, useState } from "react";
import BodyCard from "../../../components/fundamentals/body-card/body-card";
import TableViewHeader from "../../../components/fundamentals/custom-table-header";
import Table from "../../../components/fundamentals/table";
import Button from "../../../components/fundamentals/button";
import Spinner from "../../../components/fundamentals/spinner";
import useNotification from "../../../hooks/use-notification";
import useImperativeDialog from "../../../hooks/use-imperative-dialog";
import mappingsService, { BrandMappingCounts, BrandOption } from "../../../services/brand-mappings.service";
import { BrandMapping } from "../../../models/BrandMapping";
import BrandPicker from "./BrandPicker";

const VIEWS = ["Correspondances de marques"];

type Tab = "unmapped" | "auto" | "manual";

const TAB_LABELS: Record<Tab, string> = {
  unmapped: "Non rapprochées",
  auto: "Automatiques",
  manual: "Manuelles",
};

/**
 * RAG-31 — Écran « Correspondances > Marques ».
 * Onglets Non rapprochées / Automatiques / Manuelles et rapprochement manuel d'un code
 * marque externe vers une marque Ragues (s'applique aux prochains imports).
 */
export default function BrandMappingsPage() {
  const notify = useNotification();
  const confirm = useImperativeDialog();

  const [tab, setTab] = useState<Tab>("unmapped");
  const [mappings, setMappings] = useState<BrandMapping[]>([]);
  const [counts, setCounts] = useState<BrandMappingCounts>({ unmapped: 0, auto: 0, manual: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [pickerFor, setPickerFor] = useState<BrandMapping | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  async function fetchList() {
    setLoading(true);
    setError(false);
    try {
      const result = await mappingsService.list(tab, "", page);
      setMappings(result.mappings);
      setCounts(result.counts);
      setLastPage(result.lastPage);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line
  }, [tab, page]);

  function changeTab(next: Tab) {
    setPage(1);
    setTab(next);
  }

  async function handleMap(mapping: BrandMapping, brand: BrandOption) {
    setPickerFor(null);
    setBusyId(mapping.id);
    try {
      await mappingsService.update(mapping.id, brand.id);
      notify("Correspondance enregistrée", `${mapping.externalLabel} → ${brand.name}`, "success");
      await fetchList();
    } catch (e: any) {
      notify("Enregistrement refusé", e.message, e.status === 403 ? "warning" : "error");
    } finally {
      setBusyId(null);
    }
  }

  // RAG-66 : créer une marque Ragues depuis un code non rapproché (ex. une marque
  // apportée par Interactiv qui n'existe pas encore côté Ragues) puis la relier.
  async function handleCreateBrand(mapping: BrandMapping) {
    const confirmed = await confirm({
      heading: "Créer la marque",
      text: `Créer la marque « ${mapping.externalLabel} » et la relier à ce code ? Si une marque équivalente existe déjà, elle sera réutilisée.`,
      confirmText: "Oui, créer",
    });
    if (!confirmed) return;

    setBusyId(mapping.id);
    try {
      const { brandCreated } = await mappingsService.createBrand(mapping.id);
      notify(
        brandCreated ? "Marque créée" : "Marque existante réutilisée",
        `${mapping.externalLabel} → marque reliée.`,
        "success"
      );
      await fetchList();
    } catch (e: any) {
      notify("Création refusée", e.message, e.status === 403 ? "warning" : "error");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="flex flex-col grow h-full">
      <div className="w-full flex flex-col grow">
        <BodyCard customHeader={<TableViewHeader views={VIEWS} />} className="h-fit">
          <div className="flex flex-col gap-y-base">
            <p className="inter-small-regular text-grey-50">
              Rapprochez les codes marques venant d'Interactiv d'une marque Ragues. Une correspondance
              «&nbsp;manuelle&nbsp;» s'applique aux prochains imports&nbsp;; les codes non rapprochés sont
              aussi comptés dans le rapport de synchronisation.
            </p>

            <div className="flex items-center gap-x-2">
              {(Object.keys(TAB_LABELS) as Tab[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => changeTab(key)}
                  className={
                    "px-small h-10 rounded-rounded inter-small-semibold " +
                    (tab === key ? "bg-blue-60 text-grey-0" : "bg-grey-5 text-grey-70 hover:bg-grey-10")
                  }
                >
                  {TAB_LABELS[key]} ({counts[key]})
                </button>
              ))}
            </div>

            {error ? (
              <div className="py-large text-center inter-base-regular text-rose-50">
                Impossible de charger les correspondances.{" "}
                <button className="underline" onClick={fetchList}>
                  Réessayer
                </button>
              </div>
            ) : (
              <Table>
                <Table.Head>
                  <Table.HeadRow>
                    <Table.HeadCell>Source</Table.HeadCell>
                    <Table.HeadCell>Code marque externe</Table.HeadCell>
                    <Table.HeadCell>Marque cible</Table.HeadCell>
                    <Table.HeadCell>Produits</Table.HeadCell>
                    <Table.HeadCell>Origine</Table.HeadCell>
                    <Table.HeadCell></Table.HeadCell>
                  </Table.HeadRow>
                </Table.Head>
                <Table.Body>
                  {mappings.map((mapping) => (
                    <Table.Row key={mapping.id}>
                      <Table.Cell>{mapping.source}</Table.Cell>
                      <Table.Cell>{mapping.externalLabel}</Table.Cell>
                      <Table.Cell>{mapping.brand?.name ?? "—"}</Table.Cell>
                      <Table.Cell>{mapping.products_count}</Table.Cell>
                      <Table.Cell>{mapping.origin === "manual" ? "Manuelle" : "Automatique"}</Table.Cell>
                      <Table.Cell>
                        <div className="flex items-center justify-end gap-x-2">
                          {!mapping.brand_id && (
                            <Button
                              variant="secondary"
                              size="small"
                              loading={busyId === mapping.id}
                              onClick={() => handleCreateBrand(mapping)}
                            >
                              Créer la marque
                            </Button>
                          )}
                          <Button
                            variant="secondary"
                            size="small"
                            loading={busyId === mapping.id}
                            onClick={() => setPickerFor(mapping)}
                          >
                            {mapping.brand_id ? "Changer la marque" : "Rapprocher"}
                          </Button>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}

            {loading && (
              <div className="py-large flex justify-center">
                <Spinner />
              </div>
            )}
            {!loading && !error && mappings.length === 0 && (
              <div className="py-large text-center text-grey-50 inter-base-regular">
                Aucune correspondance dans cet onglet.
              </div>
            )}

            {lastPage > 1 && (
              <div className="flex items-center justify-end gap-x-2">
                <Button variant="secondary" size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Précédent
                </Button>
                <span className="inter-small-regular text-grey-50">
                  Page {page} / {lastPage}
                </span>
                <Button variant="secondary" size="small" disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}>
                  Suivant
                </Button>
              </div>
            )}
          </div>
        </BodyCard>
      </div>

      {pickerFor && (
        <BrandPicker
          onClose={() => setPickerFor(null)}
          onPick={(brand) => handleMap(pickerFor, brand)}
        />
      )}
    </div>
  );
}
